/**
 * SplatOccupancyGrid — a lightweight, CPU-side uniform occupancy grid built from a gaussian
 * splat's own point centers, weighted by per-splat OPACITY. It exists so the cursor reticle can
 * raycast the splat *surface* on scenes that have NO authored collision (and primitive-only
 * scenes), matching the old GPU-pick reticle's "works on every splat" behaviour — without a GPU
 * pick render (which in PlayCanvas 2.20 disturbs the shared unified-gsplat work buffer and causes
 * shimmer).
 *
 * OPACITY-MASS, not point count: each cell accumulates the summed (linear) opacity of the splats
 * that fall in it. A cell is "solid" only when its opacity mass clears a robust threshold derived
 * from the P90 of the nonzero-cell mass distribution (plus an absolute alpha floor). Faint airborne
 * floaters — a handful of low-α splats — never reach that threshold no matter how tightly they
 * cluster, which is the exact failure the earlier count-based grid could not catch (count saw the
 * cluster as "dense"; opacity sees it as faint). cellAlphaAt() exposes per-cell coverage in [0,1]
 * so CharacterController.sweepSurfaceT can do a sustained-opacity march (pass through thin faint
 * blobs, land on the real surface) rather than stopping at the first occupied cell.
 *
 * When no opacity is available (opacity === null), it degrades EXACTLY to the previous adaptive
 * count-density grid (mass == count·MASS_SCALE, threshold == minCount·MASS_SCALE), so behaviour is
 * unchanged on that path. Implements the same checkPointSolid / checkSphereSolid contract the
 * camera collision uses, so it drops straight into CharacterController.raycastSurface.
 *
 * Space: the grid is built in the splat's LOCAL space (resource.centers, pre-entity-transform).
 * CharacterController.worldToFile (inverse splat world matrix) maps world→that same local space,
 * so queries round-trip exactly like the voxel octree path.
 *
 * PER-SPLAT REFINEMENT (refineHitLocal): the grid caps pick precision at cellSize (extent/256 —
 * 10-20 cm on real scenes). For fine-grained picks, each brick also records WHICH splats landed in
 * it; once the march has ACCEPTED a run (floater gating already done at grid level), the hit is
 * refined against the actual gaussians in that window — α = opacity × exp(−d²/2σ²) at the ray,
 * composited front-to-back, α·T-weighted mean t. Resolution-independent, sub-cell exact.
 *
 * SPLAT-EXACT PICK (raycastSplatExactLocal): the march + refine above still ACCEPT at cell
 * granularity — thresholds calibrated to dense-surface cells make thin real objects (railings,
 * plants, props) mathematically indistinguishable from floaters, and refinement can never recover
 * a run the gate never accepted. raycastSplatExactLocal replaces acceptance itself: an
 * Amanatides–Woo DDA over the brick DIRECTORY gathers each segment's candidate splats from the
 * 27- (or 125-) brick neighborhood, t-partitioned to the segment; the actual gaussians are
 * composited front-to-back per RUN with per-run transmittance (a passed faint run does NOT
 * attenuate the wall behind it), and a run is accepted on its true composited alpha (TAU). Cells
 * and mass play no role in acceptance — the grid is pure acceleration. The cell-granular march
 * remains the fallback when per-splat opacity is unavailable (supportsExactPick=false): without
 * opacity every splat would composite as α=1 and floaters would accept instantly.
 */
// Throttle for the [refine] diagnostic (module-level: shared across grid instances).
let _refineDbgAt = 0;
// Throttle for the [pick] diagnostic (module-level: shared across grid instances).
let _pickDbgAt = 0;

/**
 * A/B rollout flag for the splat-exact pick (raycastSplatExactLocal). ON: pick acceptance runs
 * against the ACTUAL gaussians (the grid is pure acceleration) in both CharacterController.
 * sweepSurfaceT and SplatSurfaceRaycaster.raycastSurface; the cell-granular march remains only as
 * the no-opacity fallback. Flip OFF to A/B the previous cell march (+ refineHitLocal) on the same
 * scene.
 */
export const PICK_SPLAT_EXACT = true;

export class SplatOccupancyGrid {
    private readonly res: number;
    // SPARSE per-cell fixed-point opacity mass. Splats occupy a surface, so typically <1% of the
    // res³ cells are nonzero (measured 0.45% on a real scene) — a dense Uint16Array wastes ~33 MB
    // at res 256. Two-level storage instead: a directory of 8³-cell BRICKS, each brick (1 KB)
    // allocated only when a splat lands in it. Same O(1) lookups; memory ≈ 128 KB directory +
    // 1 KB × occupied bricks (~2-5 MB typical), never worse than the dense array.
    private readonly dirRes: number;      // directory resolution = ceil(res / BRICK)
    private readonly dir: Int32Array;     // brick index per directory cell, -1 = empty
    private readonly bricks: Uint16Array[];
    // Per-brick candidate splat indices (parallel to `bricks`) + RETAINED REFERENCES to the source
    // arrays they index — used by refineHitLocal to evaluate the actual gaussians near the ray.
    // References only (no copies); on the chunk-rebuild path the arrays and the grid are replaced
    // together, so indices can never drift out of alignment.
    private readonly brickSplats: Uint32Array[];
    private readonly srcCenters: Float32Array | null;
    private readonly srcOpacity: Float32Array | null;
    private readonly srcScales: Float32Array | null;   // log-space scale_0/1/2 interleaved, or null
    private readonly srcIsLogit: boolean;
    // — splat-exact pick (raycastSplatExactLocal) —
    // Per-splat quantized gaussian params, indexed by the SAME i the brick lists hold (length n;
    // only strided i are filled — wasted bytes acceptable, indexing stays trivial). Built only when
    // per-splat opacity is available (see supportsExactPick).
    private readonly alphaQ: Uint8Array | null;    // linear α × 255 (sigmoid applied ONCE at build)
    private readonly sigmaQ: Uint8Array | null;    // σ in cell/SIGMA_Q_SCALE units — disc-aware: mean of the TWO LARGEST exp(scale) axes
    private readonly gatherR: number;              // brick-neighborhood radius for the exact gather (1; 2 when σ_P95 > 2.4·cell)
    private readonly sigmaP95Cells: number;        // build-time σ P95 in cell units (drives gatherR; diagnostics)
    // Preallocated per-ray scratch — raycastSplatExactLocal is allocation-free in steady state.
    private readonly candT: Float32Array | null;
    private readonly candA: Float32Array | null;
    private readonly candSig: Float32Array | null;
    private readonly candOrd: Uint32Array | null;
    /** Stats of the last raycastSplatExactLocal call (mutated in place — no per-ray allocation). */
    readonly lastPickStats = {
        dda: 0, segs: 0, listsScanned: 0, cand: 0, evals: 0, sorted: 0, runsPassed: 0,
        accepted: false, t: 0, accA: 0, contributors: 0, sigMean: 0, gapEnd: 0, capped: false, us: 0,
    };
    private readonly thrQ: number;        // solid threshold, fixed-point mass units
    private readonly opaqQ: number;       // mass at which α_cell == 1 (for compositing)
    private readonly minX: number;
    private readonly minY: number;
    private readonly minZ: number;
    private readonly invCell: number;
    /** cell edge length in local units — exposed so callers can size a march step if useful. */
    readonly cellSize: number;
    /** number of splat points actually inserted (post-subsample). */
    readonly pointCount: number;

    // Sparse brick layout: 8³ cells per brick (Uint16 ⇒ 1 KB/brick).
    private static readonly BRICK_SHIFT = 3;
    private static readonly BRICK = 1 << SplatOccupancyGrid.BRICK_SHIFT;
    private static readonly BRICK_MASK = SplatOccupancyGrid.BRICK - 1;
    private static readonly BRICK_CELLS = SplatOccupancyGrid.BRICK ** 3;

    // Tuning — keep TAU_HIT in sync with the march's TAU_HIT (0.5).
    private static readonly MASS_SCALE = 64;   // Uint16 fixed-point; max ~1023 α-units/cell before saturation
    private static readonly ABS_FLOOR  = 2.0;  // ≥ two opaque-splats-worth of alpha/cell ⇒ kills faint floater clusters
    private static readonly REL_FRAC   = 0.35; // thr = max(ABS_FLOOR, REL_FRAC * M_surf) — stricter: rejects dense-but-faint floaters
    private static readonly P_HI       = 0.90; // percentile of nonzero cells ⇒ M_surf
    private static readonly K_RUN      = 3;    // fallback: solid-cell run length when no opacity
    private static readonly TAU_HIT    = 0.5;  // must equal the march constant

    // — splat-exact pick tuning (raycastSplatExactLocal) —
    private static readonly TAU              = 0.35;    // composite-run acceptance (engine pick-pass alphaClip analog; one solid splat alone accepts)
    private static readonly OPAQUE_T         = 0.05;    // early-out transmittance — run is ~opaque
    private static readonly ALPHA_CAND_MIN   = 1 / 255; // candidate floor
    private static readonly ALPHA_CONTRIB    = 0.02;    // only events ≥ this advance lastContribT (dust can't stitch runs)
    private static readonly GAP_K            = 4;       // gapMax = clamp(GAP_K·σ̄_run, 0.5·cell, brickExt) — "contiguous at splat scale"
    private static readonly SIGMA_MIN_FRAC   = 0.05;    // σ lower clamp, ·cell (δ-spike guard)
    private static readonly SIGMA_DEFAULT_FRAC = 0.4;   // σ when scales are unavailable, ·cell
    private static readonly SIGMA_EVAL_MAX_FRAC = 2.4;  // σ upper clamp ·cell at gatherR=1 (soundness: 3.33·σ ≤ gatherR·brickExt); ×gatherR when widened
    private static readonly SIGMA_Q_SCALE    = 32;      // sigmaQ = σ/cell × this (Uint8, cap 255)
    private static readonly MAX_SEG_CAND     = 4096;    // per-segment scratch cap
    private static readonly MAX_EVAL_PER_RAY = 8192;    // grazing-clutter cap: accept best run if ≥ TAU, else miss
    private static readonly MAX_DDA_STEPS    = 256;     // ≥ 3·dirRes worst diagonal — safety only

    constructor(centers: Float32Array, opacity: Float32Array | null = null, res = 256, stride = 1, scales: Float32Array | null = null) {
        const S = SplatOccupancyGrid;
        const n = Math.floor(centers.length / 3);

        // --- bounds ---
        let minX = Infinity, minY = Infinity, minZ = Infinity;
        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
        for (let i = 0; i < n; i += stride) {
            const x = centers[i * 3], y = centers[i * 3 + 1], z = centers[i * 3 + 2];
            if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
            if (x < minX) minX = x; if (y < minY) minY = y; if (z < minZ) minZ = z;
            if (x > maxX) maxX = x; if (y > maxY) maxY = y; if (z > maxZ) maxZ = z;
        }
        if (!Number.isFinite(minX)) {
            // No valid points — produce an empty grid that never reports solid.
            this.res = 1; this.dirRes = 1; this.dir = new Int32Array(1).fill(-1); this.bricks = [];
            this.brickSplats = []; this.srcCenters = null; this.srcOpacity = null; this.srcScales = null; this.srcIsLogit = false;
            this.alphaQ = null; this.sigmaQ = null; this.gatherR = 1; this.sigmaP95Cells = 0;
            this.candT = null; this.candA = null; this.candSig = null; this.candOrd = null;
            this.thrQ = 1; this.opaqQ = 1;
            this.minX = 0; this.minY = 0; this.minZ = 0; this.cellSize = 1; this.invCell = 1;
            this.pointCount = 0; return;
        }
        const maxExt = Math.max(maxX - minX, maxY - minY, maxZ - minZ, 1e-4);
        this.res = res; this.cellSize = maxExt / res; this.invCell = 1 / this.cellSize;
        this.minX = minX; this.minY = minY; this.minZ = minZ;
        const dirRes = Math.ceil(res / S.BRICK);
        const dir = new Int32Array(dirRes * dirRes * dirRes).fill(-1);
        const bricks: Uint16Array[] = [];
        const brickLists: number[][] = [];   // per-brick candidate splat indices (packed after pass 1)
        this.dirRes = dirRes; this.dir = dir; this.bricks = bricks;
        // Resolve (cell coords) → [brick, cellIndexInBrick, brickIndex], allocating on demand.
        const brickOf = (cx: number, cy: number, cz: number): [Uint16Array, number, number] | null => {
            if (cx < 0 || cx >= res || cy < 0 || cy >= res || cz < 0 || cz >= res) return null;
            const di = (cx >> S.BRICK_SHIFT) + (cy >> S.BRICK_SHIFT) * dirRes + (cz >> S.BRICK_SHIFT) * dirRes * dirRes;
            let bi = dir[di];
            if (bi < 0) { bi = bricks.length; bricks.push(new Uint16Array(S.BRICK_CELLS)); brickLists.push([]); dir[di] = bi; }
            return [bricks[bi], (cx & S.BRICK_MASK) + (cy & S.BRICK_MASK) * S.BRICK + (cz & S.BRICK_MASK) * S.BRICK * S.BRICK, bi];
        };

        // --- opacity availability + logit auto-detect (getProp('opacity') is LOGIT) ---
        const hasOp = !!opacity && opacity.length >= n;
        let isLogit = false;
        if (hasOp) {
            let mn = Infinity, mx = -Infinity;
            const ss = Math.max(1, Math.floor(n / 4096));
            for (let i = 0; i < n; i += ss) { const v = opacity![i]; if (v < mn) mn = v; if (v > mx) mx = v; }
            isLogit = (mn < 0) || (mx > 1.5);                 // linear [0,1] never trips this
        }

        // --- splat-exact pick per-splat params (only with opacity — see supportsExactPick) ---
        const hasScales = !!(scales && scales.length >= n * 3);
        const alphaQ = hasOp ? new Uint8Array(n) : null;      // indexed by i (strided fills; wasted bytes acceptable)
        const sigmaQ = hasOp ? new Uint8Array(n) : null;
        const SIG_DEFAULT_Q = Math.round(S.SIGMA_DEFAULT_FRAC * S.SIGMA_Q_SCALE);
        if (sigmaQ && !hasScales) sigmaQ.fill(SIG_DEFAULT_Q); // no scales anywhere → σ = SIGMA_DEFAULT for all
        const sigHist = (sigmaQ && hasScales) ? new Uint32Array(256) : null; // σ histogram → P95 → gatherR
        let sigCount = 0;

        // --- Pass 1: accumulate opacity mass (a=1 per splat when no opacity ⇒ mass == count*SCALE),
        //     recording each splat's index into its brick's candidate list for refineHitLocal ---
        let inserted = 0, occupied = 0, maxQ = 0;
        for (let i = 0; i < n; i += stride) {
            const x = centers[i * 3], y = centers[i * 3 + 1], z = centers[i * 3 + 2];
            if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
            const cx = ((x - minX) * this.invCell) | 0, cy = ((y - minY) * this.invCell) | 0, cz = ((z - minZ) * this.invCell) | 0;
            const ref = brickOf(cx, cy, cz); if (!ref) continue;
            const [brick, idx, bi] = ref;
            let a: number;
            if (hasOp) { const o = opacity![i]; a = isLogit ? 1 / (1 + Math.exp(-o)) : (o < 0 ? 0 : o > 1 ? 1 : o); }
            else a = 1;
            if (alphaQ) {
                alphaQ[i] = (a * 255 + 0.5) | 0;
                if (sigHist) {
                    // disc-aware σ: mean of the TWO LARGEST exp(scale) axes — surface splats are thin
                    // discs, and a face-on ray's perpendicular offset lives in the disc PLANE, so the
                    // mean-of-3 would underestimate σ (and thus α) by ~⅓.
                    const e0 = Math.exp(scales![i * 3]), e1 = Math.exp(scales![i * 3 + 1]), e2 = Math.exp(scales![i * 3 + 2]);
                    const eMin = e0 < e1 ? (e0 < e2 ? e0 : e2) : (e1 < e2 ? e1 : e2);
                    let sq = Math.round((e0 + e1 + e2 - eMin) * 0.5 * this.invCell * S.SIGMA_Q_SCALE);
                    if (!(sq >= 0)) sq = SIG_DEFAULT_Q; else if (sq > 255) sq = 255;   // NaN/∞ guard + Uint8 cap
                    sigmaQ![i] = sq;
                    sigHist[sq]++; sigCount++;
                }
            }
            if (brick[idx] === 0) occupied++;
            let q = brick[idx] + ((a * S.MASS_SCALE) | 0); if (q > 65535) q = 65535;
            brick[idx] = q; if (q > maxQ) maxQ = q; inserted++;
            brickLists[bi].push(i);
        }
        // Pack candidate lists (Uint32 ≈ 4 B × inserted) and retain the source references.
        this.brickSplats = brickLists.map((l) => Uint32Array.from(l));
        this.srcCenters = centers;
        this.srcOpacity = hasOp ? opacity : null;
        this.srcScales = (scales && scales.length >= n * 3) ? scales : null;
        this.srcIsLogit = isLogit;

        // --- σ_P95 → gatherR (adaptive lateral gather for big/coarse-LOD splats) + final σ clamp.
        //     Soundness: candidates contribute out to ~3.33σ, so σ must satisfy 3.33·σ ≤
        //     gatherR·brickExt or laterally-adjacent-brick splats would be silently lost. σ beyond
        //     the widened bound (rare sky blobs) is a documented, bounded truncation. ---
        let gatherR = 1, sigmaP95Cells = sigmaQ ? S.SIGMA_DEFAULT_FRAC : 0;
        if (sigHist && sigCount > 0) {
            let cum = 0, q95 = 0;
            const target = 0.95 * sigCount;
            for (let b = 0; b < 256; b++) { cum += sigHist[b]; if (cum >= target) { q95 = b; break; } }
            sigmaP95Cells = q95 / S.SIGMA_Q_SCALE;
            if (sigmaP95Cells > S.SIGMA_EVAL_MAX_FRAC) gatherR = 2;   // widen 27- → 125-neighborhood
            const loQ = Math.max(1, Math.round(S.SIGMA_MIN_FRAC * S.SIGMA_Q_SCALE));
            const hiQ = Math.min(255, Math.round(S.SIGMA_EVAL_MAX_FRAC * gatherR * S.SIGMA_Q_SCALE));
            for (let k = 0; k < sigmaQ!.length; k++) { const q = sigmaQ![k]; sigmaQ![k] = q < loQ ? loQ : q > hiQ ? hiQ : q; }
        }
        this.alphaQ = alphaQ; this.sigmaQ = sigmaQ;
        this.gatherR = gatherR; this.sigmaP95Cells = sigmaP95Cells;
        this.candT = hasOp ? new Float32Array(S.MAX_SEG_CAND) : null;
        this.candA = hasOp ? new Float32Array(S.MAX_SEG_CAND) : null;
        this.candSig = hasOp ? new Float32Array(S.MAX_SEG_CAND) : null;
        this.candOrd = hasOp ? new Uint32Array(S.MAX_SEG_CAND) : null;

        // --- Pass 2: robust threshold ---
        let thr: number, opaq: number;
        if (hasOp && maxQ > 0) {
            // log-histogram percentile over NONZERO cells (faint tail is huge in count, low in mass)
            const NB = 256, H = new Uint32Array(NB);
            const lo = Math.max(1, maxQ * 1e-4), sLog = (NB - 1) / Math.log(maxQ / lo || 1);
            let nz = 0;
            for (const brick of bricks) {   // sparse: only allocated bricks, not all res³ cells
                for (let idx = 0; idx < S.BRICK_CELLS; idx++) {
                    const m = brick[idx]; if (m === 0) continue;
                    nz++; H[m <= lo ? 0 : Math.min(NB - 1, Math.floor(Math.log(m / lo) * sLog))]++;
                }
            }
            let cum = 0, pb = 0; const target = S.P_HI * nz;
            for (let b = 0; b < NB; b++) { cum += H[b]; if (cum >= target) { pb = b; break; } }
            const M_surf = (lo * Math.exp((pb + 1) / sLog)) / S.MASS_SCALE;  // real α-mass at P90
            thr = Math.max(S.ABS_FLOOR, S.REL_FRAC * M_surf);
            opaq = M_surf;
        } else {
            // count fallback — byte-identical checkPointSolid to the old adaptive-density grid
            const meanDensity = occupied > 0 ? inserted / occupied : 1;
            const minCount = Math.max(2, Math.min(8, Math.round(meanDensity * 0.35)));
            thr = minCount;                                    // mass units == count (a=1 ⇒ SCALE/splat)
            opaq = minCount * S.K_RUN / S.TAU_HIT;             // ⇒ α_cell≈TAU/K_RUN ⇒ needs K_RUN cells
        }
        let thrQ = Math.round(thr * S.MASS_SCALE);
        if (thrQ < 1) thrQ = 1;
        // safety net: if the threshold marked NOTHING solid, fall back to any-occupancy (mass ≥ 1)
        if (maxQ < thrQ && inserted > 0) thrQ = 1;

        // TEMP diagnostic (remove once verified in-scene): hasOp=true / isLogit=true confirms per-splat
        // opacity was actually read — otherwise the grid silently used the count fallback and picks
        // stay floater-prone. thrAlpha is the solid threshold in α-mass units.
        try {
            const memKB = Math.round((dir.byteLength + bricks.length * S.BRICK_CELLS * 2) / 1024);
            const idxKB = Math.round((inserted * 4) / 1024);
            // eslint-disable-next-line no-console
            console.debug('[SplatOccupancyGrid] built', { hasOp, isLogit, hasScales: !!this.srcScales, inserted, occupied, maxQ, thrQ, thrAlpha: Math.round((thrQ / S.MASS_SCALE) * 100) / 100, bricks: bricks.length, memKB, idxKB, sigmaP95Cells: Math.round(this.sigmaP95Cells * 100) / 100, gatherR: this.gatherR, exact: this.supportsExactPick });
        } catch { /* noop */ }

        this.thrQ = thrQ;
        this.opaqQ = Math.max(1, Math.round(opaq * S.MASS_SCALE));
        this.pointCount = inserted;
    }

    private massAt(cx: number, cy: number, cz: number): number {
        const S = SplatOccupancyGrid, res = this.res;
        if (cx < 0 || cx >= res || cy < 0 || cy >= res || cz < 0 || cz >= res) return 0;
        const dirRes = this.dirRes;
        const bi = this.dir[(cx >> S.BRICK_SHIFT) + (cy >> S.BRICK_SHIFT) * dirRes + (cz >> S.BRICK_SHIFT) * dirRes * dirRes];
        if (bi < 0) return 0;
        return this.bricks[bi][(cx & S.BRICK_MASK) + (cy & S.BRICK_MASK) * S.BRICK + (cz & S.BRICK_MASK) * S.BRICK * S.BRICK];
    }

    /** True if (x,y,z) (local space) lands in a cell whose opacity mass clears the solid threshold. */
    checkPointSolid(x: number, y: number, z: number): boolean {
        return this.massAt(((x - this.minX) * this.invCell) | 0, ((y - this.minY) * this.invCell) | 0, ((z - this.minZ) * this.invCell) | 0) >= this.thrQ;
    }

    /** Per-cell coverage in [0,1] for the sustained march. 0 in empty / below-noise cells. */
    cellAlphaAt(x: number, y: number, z: number): number {
        const m = this.massAt(((x - this.minX) * this.invCell) | 0, ((y - this.minY) * this.invCell) | 0, ((z - this.minZ) * this.invCell) | 0);
        return m === 0 ? 0 : Math.min(1, m / this.opaqQ);
    }

    /** Cell edge length (local units) — lets callers size a march step so the sphere scan stays O(1). */
    queryCellSize(): number {
        return this.cellSize;
    }

    /** True when the splat-exact pick path is available: per-splat opacity + retained centers. */
    get supportsExactPick(): boolean {
        return this.alphaQ !== null && this.srcCenters !== null;
    }

    /**
     * True if ≥2 thresholded-solid cells lie within `radius` (local units) of (x,y,z). The scanned
     * cell range is CLAMPED to the grid, so a query outside the grid (or near an edge) is O(1). The
     * ≥2 requirement means a lone surviving cell still doesn't read as a surface.
     */
    checkSphereSolid(x: number, y: number, z: number, radius: number): boolean {
        const res = this.res, thrQ = this.thrQ;
        const rc = Math.max(0, Math.ceil(radius * this.invCell));
        const cx = ((x - this.minX) * this.invCell) | 0, cy = ((y - this.minY) * this.invCell) | 0, cz = ((z - this.minZ) * this.invCell) | 0;
        const x0 = Math.max(0, cx - rc), x1 = Math.min(res - 1, cx + rc); if (x0 > x1) return false;
        const y0 = Math.max(0, cy - rc), y1 = Math.min(res - 1, cy + rc); if (y0 > y1) return false;
        const z0 = Math.max(0, cz - rc), z1 = Math.min(res - 1, cz + rc); if (z0 > z1) return false;
        let hits = 0;
        for (let zz = z0; zz <= z1; zz++) {
            for (let yy = y0; yy <= y1; yy++) {
                for (let xx = x0; xx <= x1; xx++) {
                    if (this.massAt(xx, yy, zz) >= thrQ && ++hits >= 2) return true;
                }
            }
        }
        return false;
    }

    /** Linear [0,1] alpha of splat i (sigmoid when the stored opacity is logit); 1 when no opacity. */
    private splatAlpha(i: number): number {
        const op = this.srcOpacity;
        if (!op) return 1;
        const o = op[i];
        return this.srcIsLogit ? 1 / (1 + Math.exp(-o)) : (o < 0 ? 0 : o > 1 ? 1 : o);
    }

    /** gapMax for the CURRENT run: clamp(GAP_K·σ̄_run, 0.5·cell, brickExt) — "contiguous at splat
     *  scale". σ grows on coarse LOD, so gaps stretch automatically; the default holds until the
     *  run has its first contributing (≥ ALPHA_CONTRIB) event. */
    private gapMaxLocal(sigW: number, sigWSum: number): number {
        const S = SplatOccupancyGrid, cell = this.cellSize;
        const g = S.GAP_K * (sigWSum > 0 ? sigW / sigWSum : S.SIGMA_DEFAULT_FRAC * cell);
        const lo = 0.5 * cell, hi = S.BRICK * cell;
        return g < lo ? lo : g > hi ? hi : g;
    }

    /** Fill the accepted-run fields of lastPickStats (accept paths of raycastSplatExactLocal). */
    private recordAccept(accA: number, contributors: number, sigW: number, sigWSum: number): void {
        const st = this.lastPickStats;
        st.accA = accA;
        st.contributors = contributors;
        st.sigMean = sigWSum > 0 ? sigW / sigWSum : 0;
        st.gapEnd = this.gapMaxLocal(sigW, sigWSum);
    }

    /** Finalize lastPickStats + the throttled [pick] diagnostic; returns tHit unchanged. */
    private finishPick(tHit: number | null, tStartMs: number): number | null {
        const st = this.lastPickStats;
        st.accepted = tHit != null;
        st.t = tHit ?? 0;
        const hasPerf = typeof performance !== 'undefined';
        st.us = hasPerf ? Math.round((performance.now() - tStartMs) * 1000) : 0;
        if (hasPerf && performance.now() - _pickDbgAt > 1000) {
            _pickDbgAt = performance.now();
            try {
                // eslint-disable-next-line no-console
                console.debug('[pick]', {
                    dda: st.dda, segs: st.segs, lists: st.listsScanned, cand: st.cand, evals: st.evals,
                    sorted: st.sorted, runsPassed: st.runsPassed,
                    accepted: tHit != null
                        ? { t: +st.t.toFixed(4), accA: +st.accA.toFixed(3), contributors: st.contributors, sigMean: +st.sigMean.toFixed(4), gapEnd: +st.gapEnd.toFixed(4) }
                        : null,
                    capped: st.capped, us: st.us,
                });
            } catch { /* noop */ }
        }
        return tHit;
    }

    /**
     * Splat-EXACT pick: composite the ACTUAL gaussians along a LOCAL-space ray and return the
     * α·T-weighted mean t of the first run whose composited alpha clears TAU, or null on a miss.
     * The grid is pure acceleration here — cells, mass and thresholds play no role in acceptance,
     * which is what lets thin real objects (railings, plants, props) be picked while faint floater
     * clusters are passed through on their true alpha.
     *
     * Contract: (ox,oy,oz)+(dx,dy,dz) are LOCAL space, dir NORMALIZED IN LOCAL SPACE, tMax in local
     * units. Callers must gate on supportsExactPick; an exact miss must be treated as a MISS (no
     * fall-through to the cell march, which would re-introduce cell-granular acceptance).
     *
     * Structure: Amanatides–Woo DDA over the 32³ brick DIRECTORY. Per directory-cell segment
     * [segT0, segT1), candidates are gathered from the (2·gatherR+1)³ brick neighborhood and
     * filtered to tI ∈ [segT0, segT1) — each splat lives in exactly ONE brick list, so this
     * t-partition both guarantees global front-to-back ordering AND makes a dedup set unnecessary
     * (a splat gathered by 2–3 consecutive neighborhoods is kept only by the segment containing its
     * closest-approach t). Do not "optimize" the t-filter away. Run state lives at RAY scope (runs
     * span segments and empty bricks); transmittance is per-RUN, so a passed faint run cannot
     * attenuate the surface behind it. Zero allocation per ray (preallocated scratch).
     */
    raycastSplatExactLocal(ox: number, oy: number, oz: number,
        dx: number, dy: number, dz: number, tMax: number): number | null {
        const S = SplatOccupancyGrid;
        const centers = this.srcCenters, alphaQ = this.alphaQ, sigmaQ = this.sigmaQ;
        const candT = this.candT, candA = this.candA, candSig = this.candSig, candOrd = this.candOrd;
        if (!centers || !alphaQ || !sigmaQ || !candT || !candA || !candSig || !candOrd || this.bricks.length === 0) return null;

        const tStartMs = typeof performance !== 'undefined' ? performance.now() : 0;
        const st = this.lastPickStats;
        st.dda = 0; st.segs = 0; st.listsScanned = 0; st.cand = 0; st.evals = 0; st.sorted = 0;
        st.runsPassed = 0; st.accA = 0; st.contributors = 0; st.sigMean = 0; st.gapEnd = 0; st.capped = false;

        const cell = this.cellSize, brickExt = S.BRICK * cell;
        const dirRes = this.dirRes, dir = this.dir, brickSplats = this.brickSplats;
        const minX = this.minX, minY = this.minY, minZ = this.minZ;
        const ext = dirRes * brickExt;   // directory AABB spans dirRes bricks (≥ res·cell; pad cells are empty)

        // ── ray ∩ grid AABB (slab), clipped to [0, tMax]; degenerate axes (|d| ≤ 1e-12) are
        //    slab-membership-tested only and never advanced by the DDA (tDelta = ∞). ──
        let t0 = 0, t1 = tMax;
        if (dx > 1e-12 || dx < -1e-12) {
            const inv = 1 / dx;
            let ta = (minX - ox) * inv, tb = (minX + ext - ox) * inv;
            if (ta > tb) { const tt = ta; ta = tb; tb = tt; }
            if (ta > t0) t0 = ta;
            if (tb < t1) t1 = tb;
        } else if (ox < minX || ox > minX + ext) return this.finishPick(null, tStartMs);
        if (dy > 1e-12 || dy < -1e-12) {
            const inv = 1 / dy;
            let ta = (minY - oy) * inv, tb = (minY + ext - oy) * inv;
            if (ta > tb) { const tt = ta; ta = tb; tb = tt; }
            if (ta > t0) t0 = ta;
            if (tb < t1) t1 = tb;
        } else if (oy < minY || oy > minY + ext) return this.finishPick(null, tStartMs);
        if (dz > 1e-12 || dz < -1e-12) {
            const inv = 1 / dz;
            let ta = (minZ - oz) * inv, tb = (minZ + ext - oz) * inv;
            if (ta > tb) { const tt = ta; ta = tb; tb = tt; }
            if (ta > t0) t0 = ta;
            if (tb < t1) t1 = tb;
        } else if (oz < minZ || oz > minZ + ext) return this.finishPick(null, tStartMs);
        if (!(t1 > t0)) return this.finishPick(null, tStartMs);

        // ── Amanatides–Woo DDA init over the brick directory ──
        const px = ox + dx * t0, py = oy + dy * t0, pz = oz + dz * t0;
        let ix = Math.floor((px - minX) / brickExt); if (ix < 0) ix = 0; else if (ix >= dirRes) ix = dirRes - 1;
        let iy = Math.floor((py - minY) / brickExt); if (iy < 0) iy = 0; else if (iy >= dirRes) iy = dirRes - 1;
        let iz = Math.floor((pz - minZ) / brickExt); if (iz < 0) iz = 0; else if (iz >= dirRes) iz = dirRes - 1;
        let stepX = 0, tDeltaX = Infinity, tMaxX = Infinity;
        if (dx > 1e-12)       { stepX = 1;  tDeltaX = brickExt / dx;  tMaxX = t0 + ((ix + 1) * brickExt + minX - px) / dx; }
        else if (dx < -1e-12) { stepX = -1; tDeltaX = -brickExt / dx; tMaxX = t0 + (ix * brickExt + minX - px) / dx; }
        let stepY = 0, tDeltaY = Infinity, tMaxY = Infinity;
        if (dy > 1e-12)       { stepY = 1;  tDeltaY = brickExt / dy;  tMaxY = t0 + ((iy + 1) * brickExt + minY - py) / dy; }
        else if (dy < -1e-12) { stepY = -1; tDeltaY = -brickExt / dy; tMaxY = t0 + (iy * brickExt + minY - py) / dy; }
        let stepZ = 0, tDeltaZ = Infinity, tMaxZ = Infinity;
        if (dz > 1e-12)       { stepZ = 1;  tDeltaZ = brickExt / dz;  tMaxZ = t0 + ((iz + 1) * brickExt + minZ - pz) / dz; }
        else if (dz < -1e-12) { stepZ = -1; tDeltaZ = -brickExt / dz; tMaxZ = t0 + (iz * brickExt + minZ - pz) / dz; }

        const gr = this.gatherR;
        const sigScale = cell / S.SIGMA_Q_SCALE, inv255 = 1 / 255;
        // Run state lives at RAY scope — a run can span multiple segments/bricks. Per-RUN
        // transmittance (T resets with the run): a passed faint run must NOT attenuate the wall
        // behind it. lastContribT deliberately survives run resets: a dust-only "run" then gap-fails
        // immediately, so 1/255-level dust can neither stitch runs together nor hold one open.
        let inRun = false, T = 1, accA = 0, wSum = 0, wtSum = 0;
        let lastContribT = -Infinity, sigW = 0, sigWSum = 0, runContrib = 0;
        let segT0 = t0;

        for (let stepI = 0; stepI < S.MAX_DDA_STEPS; stepI++) {
            st.dda++;
            let segT1 = tMaxX < tMaxY ? (tMaxX < tMaxZ ? tMaxX : tMaxZ) : (tMaxY < tMaxZ ? tMaxY : tMaxZ);
            const lastSeg = segT1 >= t1;
            if (lastSeg) segT1 = t1;

            // ── gather this segment's candidates, t-partitioned to [segT0, segT1) ──
            let m = 0;
            const x0 = ix - gr < 0 ? 0 : ix - gr, x1 = ix + gr >= dirRes ? dirRes - 1 : ix + gr;
            const y0 = iy - gr < 0 ? 0 : iy - gr, y1 = iy + gr >= dirRes ? dirRes - 1 : iy + gr;
            const z0 = iz - gr < 0 ? 0 : iz - gr, z1 = iz + gr >= dirRes ? dirRes - 1 : iz + gr;
            gather:
            for (let zz = z0; zz <= z1; zz++) {
                const zOff = zz * dirRes * dirRes;
                for (let yy = y0; yy <= y1; yy++) {
                    const yOff = zOff + yy * dirRes;
                    for (let xx = x0; xx <= x1; xx++) {
                        const bi = dir[yOff + xx];
                        if (bi < 0) continue;
                        st.listsScanned++;
                        const list = brickSplats[bi];
                        for (let k = 0; k < list.length; k++) {
                            const i = list[k], i3 = i * 3;
                            const rx = centers[i3] - ox, ry = centers[i3 + 1] - oy, rz = centers[i3 + 2] - oz;
                            const tI = rx * dx + ry * dy + rz * dz;              // closest-approach t
                            if (tI < segT0 || tI >= segT1) continue;             // t-partition (ordering + dedup)
                            let dd = rx * rx + ry * ry + rz * rz - tI * tI;      // perpendicular²
                            if (dd < 0) dd = 0;
                            const sig = sigmaQ[i] * sigScale;
                            const a = alphaQ[i] * inv255 * Math.exp(-dd / (2 * sig * sig));
                            if (a < S.ALPHA_CAND_MIN) continue;
                            if (m >= S.MAX_SEG_CAND || ++st.evals > S.MAX_EVAL_PER_RAY) { st.capped = true; break gather; }
                            candT[m] = tI; candA[m] = a; candSig[m] = sig; m++;
                        }
                    }
                }
            }
            if (st.capped) break;   // partial gather dropped — straight to the FINISH accept check

            if (m === 0) {
                // Run may end ACROSS empty bricks — gap check at segment entry.
                if (inRun && (segT0 - lastContribT) > this.gapMaxLocal(sigW, sigWSum)) {
                    if (accA >= S.TAU && wSum > 0) {
                        this.recordAccept(accA, runContrib, sigW, sigWSum);
                        return this.finishPick(wtSum / wSum, tStartMs);
                    }
                    st.runsPassed++;                     // faint run (floater) — passed through
                    inRun = false;
                }
            } else {
                st.segs++; st.cand += m;
                // Within-segment insertion index-sort by t (segments hold tens–hundreds of events).
                for (let j = 0; j < m; j++) candOrd[j] = j;
                for (let j = 1; j < m; j++) {
                    const oj = candOrd[j], tj = candT[oj];
                    let p = j - 1;
                    while (p >= 0 && candT[candOrd[p]] > tj) { candOrd[p + 1] = candOrd[p]; p--; }
                    candOrd[p + 1] = oj;
                }
                st.sorted += m;
                // Front-to-back compositing with per-RUN transmittance.
                for (let j = 0; j < m; j++) {
                    const cj = candOrd[j], tE = candT[cj], aE = candA[cj];
                    if (inRun && (tE - lastContribT) > this.gapMaxLocal(sigW, sigWSum)) {
                        if (accA >= S.TAU && wSum > 0) {
                            this.recordAccept(accA, runContrib, sigW, sigWSum);
                            return this.finishPick(wtSum / wSum, tStartMs);   // accepted surface ends before this event
                        }
                        st.runsPassed++;
                        inRun = false;
                    }
                    if (!inRun) { inRun = true; T = 1; accA = 0; wSum = 0; wtSum = 0; sigW = 0; sigWSum = 0; runContrib = 0; }
                    const w = T * aE;
                    wSum += w; wtSum += w * tE; accA += w; T *= (1 - aE);
                    if (aE >= S.ALPHA_CONTRIB) { lastContribT = tE; sigW += w * candSig[cj]; sigWSum += w; runContrib++; }
                    if (T <= S.OPAQUE_T) {
                        this.recordAccept(accA, runContrib, sigW, sigWSum);
                        return this.finishPick(wtSum / wSum, tStartMs);       // run is ~opaque — early out
                    }
                }
            }

            if (lastSeg) break;
            segT0 = segT1;
            // Advance the axis with the nearest boundary.
            if (tMaxX <= tMaxY && tMaxX <= tMaxZ) { ix += stepX; tMaxX += tDeltaX; if (ix < 0 || ix >= dirRes) break; }
            else if (tMaxY <= tMaxZ)              { iy += stepY; tMaxY += tDeltaY; if (iy < 0 || iy >= dirRes) break; }
            else                                  { iz += stepZ; tMaxZ += tDeltaZ; if (iz < 0 || iz >= dirRes) break; }
        }

        // FINISH — grid exit / tMax / caps: accept an open run that already cleared TAU.
        if (inRun && accA >= S.TAU && wSum > 0) {
            this.recordAccept(accA, runContrib, sigW, sigWSum);
            return this.finishPick(wtSum / wSum, tStartMs);
        }
        return this.finishPick(null, tStartMs);
    }

    /**
     * Refine a hit within [tFront, tBack] (LOCAL-space ray, dir normalized) against the ACTUAL
     * gaussians: gather candidate splats from the bricks the padded segment passes through, evaluate
     * each one's alpha at the ray (opacity × exp(−d²/2σ²), σ from per-splat scales when available),
     * depth-sort, composite front-to-back, and return the α·T-weighted mean t — resolution-
     * independent, sub-cell exact. Returns null when nothing contributes (caller falls back to the
     * grid-cell mean, never regressing). MUST only be called inside a march-ACCEPTED run window:
     * refinement never widens the window, so floater gating cannot regress.
     * tGridLocal/worldPerLocal are diagnostics-only (grid-vs-refined delta in world meters).
     */
    refineHitLocal(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number,
        tFront: number, tBack: number, tGridLocal?: number, worldPerLocal?: number): number | null {
        const S = SplatOccupancyGrid;
        const centers = this.srcCenters;
        if (!centers || this.bricks.length === 0 || !(tBack > tFront)) return null;
        const cell = this.cellSize, pad = 2 * cell;
        const brickExt = S.BRICK * cell;
        const dirRes = this.dirRes, res = this.res, invCell = this.invCell;
        const scales = this.srcScales;

        // Gather candidates from the bricks the padded segment passes through (padded one brick each
        // side, half-brick steps so corners aren't skipped; dedupe by directory index).
        const seen = new Set<number>();
        const cand: number[] = [];
        const t0 = tFront - pad - brickExt, t1 = tBack + pad + brickExt;
        const step = brickExt * 0.5;
        let walkIters = 0;
        for (let t = t0; t <= t1 + step * 0.5 && walkIters < 256; t += step, walkIters++) {
            const px = ox + dx * t, py = oy + dy * t, pz = oz + dz * t;
            const cx = ((px - this.minX) * invCell) | 0, cy = ((py - this.minY) * invCell) | 0, cz = ((pz - this.minZ) * invCell) | 0;
            if (cx < 0 || cx >= res || cy < 0 || cy >= res || cz < 0 || cz >= res) continue;
            const di = (cx >> S.BRICK_SHIFT) + (cy >> S.BRICK_SHIFT) * dirRes + (cz >> S.BRICK_SHIFT) * dirRes * dirRes;
            if (seen.has(di)) continue;
            seen.add(di);
            const bi = this.dir[di];
            if (bi < 0) continue;
            const list = this.brickSplats[bi];
            for (let k = 0; k < list.length; k++) cand.push(list[k]);
            if (cand.length > 16384) break;   // bound the worst case; candidates are cheap but not free
        }
        if (cand.length === 0) return null;

        // Evaluate each candidate gaussian at the ray → {t, α} (flat parallel arrays, no objects).
        const sigDefault = 0.4 * cell, sigMin = 0.05 * cell, sigMax = 4 * cell;
        const winLo = tFront - pad, winHi = tBack + pad;
        const ts: number[] = [], alphas: number[] = [];
        for (let k = 0; k < cand.length; k++) {
            const i = cand[k];
            const rx = centers[i * 3] - ox, ry = centers[i * 3 + 1] - oy, rz = centers[i * 3 + 2] - oz;
            const tI = rx * dx + ry * dy + rz * dz;             // depth of closest approach along the ray
            if (tI < winLo || tI > winHi) continue;
            const dd = rx * rx + ry * ry + rz * rz - tI * tI;   // squared perpendicular distance to the ray
            let sig = sigDefault;
            if (scales) {
                const s = (Math.exp(scales[i * 3]) + Math.exp(scales[i * 3 + 1]) + Math.exp(scales[i * 3 + 2])) / 3;
                sig = s < sigMin ? sigMin : s > sigMax ? sigMax : s;
            }
            const a = this.splatAlpha(i) * Math.exp(-(dd > 0 ? dd : 0) / (2 * sig * sig));
            if (a < 1 / 255) continue;
            ts.push(tI); alphas.push(a);
        }
        if (ts.length === 0) return null;

        // Depth-sort and composite front-to-back — SuperSplat's depth-pick math over real gaussians.
        const order = ts.map((_, j) => j).sort((x, y) => ts[x] - ts[y]);
        let trans = 1, wSum = 0, wtSum = 0;
        for (let j = 0; j < order.length; j++) {
            const o = order[j], a = alphas[o];
            const w = trans * a;
            wSum += w; wtSum += w * ts[o];
            trans *= (1 - a);
            if (trans <= 0.05) break;
        }
        if (!(wSum > 1e-4)) return null;
        const tRefined = wtSum / wSum;

        // TEMP diagnostic (throttled): proves refinement engages + shows the precision gain vs the grid.
        if (typeof performance !== 'undefined' && performance.now() - _refineDbgAt > 1000) {
            _refineDbgAt = performance.now();
            const w = worldPerLocal ?? 1;
            try {
                // eslint-disable-next-line no-console
                console.debug('[refine]', {
                    candidates: ts.length,
                    tGrid: tGridLocal != null ? +(tGridLocal * w).toFixed(3) : null,
                    tRefined: +(tRefined * w).toFixed(3),
                    deltaM: tGridLocal != null ? +Math.abs((tRefined - tGridLocal) * w).toFixed(4) : null,
                });
            } catch { /* noop */ }
        }
        return tRefined;
    }
}
